import fetch from "../utils/authenticatedFetch";
import UserAvatar from "./UserAvatar";
import {roleLabel} from "./ApplicationJourney";
import ModalLayer from './ModalLayer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MessagesPage.css';
import {
  FaCheck,
  FaEnvelopeOpen,
  FaPaperclip,
  FaPaperPlane,
  FaPlus,
  FaRedo,
  FaSearch,
  FaTicketAlt,
  FaTimes,
  FaUserCheck,
} from 'react-icons/fa';
import { useAuth } from 'contexts/AuthContext';

type ConversationStatus = 'OPEN' | 'ASSIGNED' | 'IN_PROGRESS' | 'RESOLVED' | 'PENDING' | 'CLOSED';
type ConversationType = 'APPLICANT_SUPPORT' | 'USER_SUPPORT' | 'APPLICATION_SUPPORT' | 'SUPPORT_TICKET' | 'DIRECT_MESSAGE';

interface Participant {
  id?: number;
  userId: number;
  userName: string;
  active: boolean;
  lastReadAt?: string | null;
}

interface Message {
  id: number;
  conversationId: number;
  senderUserId: number;
  senderUserName: string;
  senderRole?: string;
  body: string;
  sentAt: string;
  unread?: boolean;
  attachmentOriginalFileName?: string | null;
  attachmentDownloadUrl?: string | null;
  attachmentFileSize?: number | null;
}

interface Conversation {
  id: number;
  title?: string;
  subject: string;
  status: ConversationStatus;
  conversationType: ConversationType;
  researchApplicationId?: number | null;
  assignedSupportUserId?: number | null;
  assignedSupportUserName?: string | null;
  createdByUserId: number;
  createdByUserName?: string | null;
  createdAt?: string;
  updatedAt?: string;
  lastMessageAt?: string;
  lastMessagePreview?: string;
  unreadCount?: number;
  participants?: Participant[];
  messages?: Message[];
  hasMoreMessages?: boolean;
}

interface UserOption {
  id: number;
  name?: string;
  surname?: string;
  userType?: string;
  status?: string;
}

interface Toast {
  id: number;
  message: string;
}

const SUPPORT_ROLES = new Set(['ADMIN_CYBER']);
const MAX_BODY_LENGTH = 4000;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg']);

const MessagesPage: React.FC<{mode?:'support'|'direct'}> = ({mode='support'}) => {
  const { account } = useAuth();
  const currentUser = account as any;
  const currentUserId = Number(currentUser?.id);
  const currentUserType = String(currentUser?.userType || '');
  const isSupportUser = SUPPORT_ROLES.has(currentUserType);
  const canDirect = ['ADMIN','ADMIN_CYBER','RESEARCH_OFFICER'].includes(currentUserType);

  const [mineOnly,setMineOnly]=useState(false);
  const [messagePage,setMessagePage]=useState(0);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ConversationStatus>('ALL');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [composerBody, setComposerBody] = useState('');
  const [composerAttachment, setComposerAttachment] = useState<File | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newApplicationId, setNewApplicationId] = useState('');
  const [newType, setNewType] = useState<ConversationType>('USER_SUPPORT');
  const [directRecipientId, setDirectRecipientId] = useState('');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);

  const supportUsers = useMemo(
    () => users.filter((user) => SUPPORT_ROLES.has(String(user.userType || '')) && user.status === 'ACTIVE'),
    [users]
  );

  const directMessageUsers = useMemo(
    () => users.filter((user) => user.id !== currentUserId && user.status !== 'DECLINED'),
    [users, currentUserId]
  );

  const addToast = useCallback((message: string) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { id, message }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((toast) => toast.id !== id)), 3200);
  }, []);

  const getErrorMessage = async (response: Response, fallback: string) => {
    try {
      const data = await response.json();
      const reference = data?.traceId ? ` Reference: ${data.traceId}.` : '';
      return `${data?.message || data?.error || fallback}${reference}`;
    } catch {
      return fallback;
    }
  };

  const fetchConversations = useCallback(async (targetPage = 0, append = false) => {
    if (!currentUserId) return;
    setLoadingList(true);
    try {
      const params = new URLSearchParams({
        userId: String(currentUserId),
        page: String(targetPage),
        size: '20',
        kind: mode==='direct'?'DIRECT':'SUPPORT',
        mine:String(mineOnly),
      });
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      const response = await fetch(`/v1/api/support/conversations?${params.toString()}`);
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to load conversations.'));
      const payload = await response.json();
      const pageData = payload?.data;
      const content: Conversation[] = (Array.isArray(pageData?.content) ? pageData.content : []).filter((c:Conversation)=>mode==='direct'?c.conversationType==='DIRECT_MESSAGE':c.conversationType!=='DIRECT_MESSAGE');
      setConversations((prev) => (append ? [...prev, ...content] : content));
      setHasMore(!pageData?.last);
      setPage(targetPage);
      if (!selectedConversationId) {const deepId=Number(new URLSearchParams(window.location.search).get('conversation'));if(deepId)setSelectedConversationId(deepId);}
    } catch (error: any) {
      addToast(error?.message || 'Unable to load conversations.');
    } finally {
      setLoadingList(false);
    }
  }, [addToast, currentUserId, search, selectedConversationId, statusFilter, mode, mineOnly]);

  const fetchConversation = useCallback(async (conversationId: number, quiet = false, olderPage = 0) => {
    if (!currentUserId) return;
    if (!quiet) setLoadingThread(true);
    try {
      const response = await fetch(`/v1/api/support/conversations/${conversationId}?userId=${currentUserId}&page=${olderPage}&size=30`);
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to open this conversation.'));
      const payload = await response.json();
      const conversation: Conversation = payload?.data;
      setSelectedConversation(previous=>olderPage&&previous?{...conversation,messages:[...(conversation.messages||[]),...(previous.messages||[])]}:conversation);
      setMessagePage(olderPage);
      setConversations((prev) => prev.map((item) => (item.id === conversation.id ? { ...item, ...conversation } : item)));
      await fetch(`/v1/api/support/conversations/${conversationId}/read?userId=${currentUserId}`, { method: 'PUT' });
    } catch (error: any) {
      if (!quiet) addToast(error?.message || 'Unable to open this conversation.');
    } finally {
      if (!quiet) setLoadingThread(false);
    }
  }, [addToast, currentUserId]);

  useEffect(() => {
    fetchConversations(0, false);
  }, [fetchConversations]);

  useEffect(() => {
    if (!selectedConversationId) {
      setSelectedConversation(null);
      return;
    }
    fetchConversation(selectedConversationId);
  }, [fetchConversation, selectedConversationId]);

  useEffect(() => {
    if (!currentUserId) return undefined;
    const interval = window.setInterval(() => {
      fetchConversations(0, false);
      if (selectedConversationId && messagePage===0) fetchConversation(selectedConversationId, true);
    }, 30000);
    return () => window.clearInterval(interval);
  }, [currentUserId, fetchConversation, fetchConversations, selectedConversationId, messagePage]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages?.length]);

  useEffect(() => {
    if ((!isSupportUser&&!canDirect) || users.length) return;
    fetch('/v1/api/users/all')
      .then((response) => response.ok ? response.json() : Promise.reject(response))
      .then((payload) => setUsers(Array.isArray(payload?.data) ? payload.data : []))
      .catch(() => addToast('Unable to load user list for support assignment.'));
  }, [addToast, isSupportUser, canDirect, users.length]);

  const resetNewConversationForm = () => {
    setNewSubject('');
    setNewBody('');
    setNewApplicationId('');
    setNewType(currentUserType === 'APPLICANT' ? 'APPLICANT_SUPPORT' : 'USER_SUPPORT');
    setDirectRecipientId('');
  };

  const handleOpenNewModal = () => {
    resetNewConversationForm();
    setShowNewModal(true);
  };

  const handleCreateConversation = async () => {
    if (!currentUserId) return;
    if (!newSubject.trim() || !newBody.trim()) {
      addToast('Subject and message are required.');
      return;
    }
    if (newBody.trim().length > MAX_BODY_LENGTH) {
      addToast(`Message must be ${MAX_BODY_LENGTH} characters or fewer.`);
      return;
    }
    const participantUserIds = newType === 'DIRECT_MESSAGE' && directRecipientId
      ? [Number(directRecipientId)]
      : undefined;
    try {
      const response = await fetch('/v1/api/support/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: newSubject.trim(),
          initialMessage: newBody.trim(),
          createdByUserId: currentUserId,
          conversationType: newType,
          researchApplicationId: newApplicationId ? Number(newApplicationId) : undefined,
          participantUserIds,
        }),
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to create conversation.'));
      const payload = await response.json();
      const conversation: Conversation = payload?.data;
      setConversations((prev) => [conversation, ...prev.filter((item) => item.id !== conversation.id)]);
      setSelectedConversationId(conversation.id);
      setShowNewModal(false);
      resetNewConversationForm();
      addToast('Conversation created.');
    } catch (error: any) {
      addToast(error?.message || 'Unable to create conversation.');
    }
  };

  const handleSend = async () => {
    if (!selectedConversation || !currentUserId || sending) return;
    if (!composerBody.trim()) {
      addToast('Message body is required.');
      return;
    }
    if (composerBody.trim().length > MAX_BODY_LENGTH) {
      addToast(`Message must be ${MAX_BODY_LENGTH} characters or fewer.`);
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('senderUserId', String(currentUserId));
      formData.append('body', composerBody.trim());
      if (composerAttachment) formData.append('attachment', composerAttachment);
      const response = await fetch(`/v1/api/support/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to send message.'));
      setComposerBody('');
      setComposerAttachment(null);
      await fetchConversation(selectedConversation.id, true);
      await fetchConversations(0, false);
    } catch (error: any) {
      addToast(error?.message || 'Unable to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentChange = (file: File | undefined) => {
    if (!file) {
      setComposerAttachment(null);
      return;
    }
    const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';
    if (!extension || !ALLOWED_ATTACHMENT_EXTENSIONS.has(extension)) {
      addToast('Attachment type is not allowed.');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      addToast('Attachment exceeds the 10 MiB per-file limit.');
      return;
    }
    setComposerAttachment(file);
  };

  const handleStatusAction = async (action: 'close' | 'reopen' | 'resolve' | 'release') => {
    if (!selectedConversation || !currentUserId) return;
    try {
      const response = await fetch(`/v1/api/support/conversations/${selectedConversation.id}/${action}?userId=${currentUserId}`, {
        method: 'PUT',
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, `Unable to ${action} conversation.`));
      await fetchConversation(selectedConversation.id, true);
      await fetchConversations(0, false);
    } catch (error: any) {
      addToast(error?.message || `Unable to ${action} conversation.`);
    }
  };

  const handleAssign = async (assignedSupportUserId: string) => {
    if (!selectedConversation || !currentUserId || !assignedSupportUserId) return;
    try {
      const params = new URLSearchParams({
        userId: String(currentUserId),
        assignedSupportUserId,
      });
      const response = await fetch(`/v1/api/support/conversations/${selectedConversation.id}/assign?${params.toString()}`, {
        method: 'PUT',
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to assign support agent.'));
      await fetchConversation(selectedConversation.id, true);
      await fetchConversations(0, false);
      addToast('Support agent assigned.');
    } catch (error: any) {
      addToast(error?.message || 'Unable to assign support agent.');
    }
  };

  const loadMore = () => {
    if (!loadingList && hasMore) fetchConversations(page + 1, true);
  };

  const selectedMessages = selectedConversation?.messages || [];
  const selectedIsClosed = ['CLOSED','RESOLVED'].includes(selectedConversation?.status || '');
  const canReply = !!selectedConversation && !selectedIsClosed && (selectedConversation.conversationType==='DIRECT_MESSAGE' || selectedConversation.createdByUserId===currentUserId || selectedConversation.assignedSupportUserId===currentUserId);
  const canManageSelected = !!selectedConversation && (isSupportUser || selectedConversation.assignedSupportUserId===currentUserId || selectedConversation.createdByUserId === currentUserId);

  if (!currentUserId) {
    return <div className="messages-state">Sign in again to view messages.</div>;
  }

  return (
    <div className={`messages-root ${selectedConversationId?'has-thread':'no-thread'}`}>
      <div className="messages-toast-region" aria-live="polite">
        {toasts.map((toast) => (
          <div className="messages-toast" key={toast.id}>
            <span>{toast.message}</span>
            <button onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))} aria-label="Dismiss">x</button>
          </div>
        ))}
      </div>

      <aside className="messages-sidebar">
        <div className="messages-sidebar-header">
          <div>
            <span>Chats</span>
            <small>Support and application conversations</small>
          </div>
          <button className="messages-icon-btn primary" title="New conversation" onClick={handleOpenNewModal}>
            <FaPlus />
          </button>
        </div>

        <div className="messages-filter-bar">
          {isSupportUser&&mode==='support'&&<label><input type="checkbox" checked={mineOnly} onChange={e=>setMineOnly(e.target.checked)}/>My support cases</label>}
          <label className="messages-search">
            <FaSearch />
            <input aria-label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
            />
          </label>
          <select aria-label="Conversation status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as any)}>
            <option value="ALL">All</option>
            <option value="OPEN">Open</option>
            <option value="ASSIGNED">Assigned</option><option value="IN_PROGRESS">In progress</option><option value="RESOLVED">Resolved</option><option value="PENDING">Pending (legacy)</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>

        <div className="messages-conv-list">
          {loadingList && conversations.length === 0 ? (
            <div className="messages-state">Loading conversations...</div>
          ) : conversations.length === 0 ? (
            <div className="messages-empty-list">
              <FaEnvelopeOpen />
              <span>No conversations found.</span>
            </div>
          ) : (
            conversations.map((conversation) => (
              <button
                className={`messages-conv-item${selectedConversationId === conversation.id ? ' selected' : ''}`}
                key={conversation.id}
                onClick={() => {if(conversation.assignedSupportUserId&&conversation.assignedSupportUserId!==currentUserId&&conversation.createdByUserId!==currentUserId&&conversation.conversationType!=='DIRECT_MESSAGE'){addToast('Assigned to '+conversation.assignedSupportUserName+'. Only the requester and assigned agent can open this case.');return;}setSelectedConversationId(conversation.id);}}
              >
                <span className="messages-conv-icon">
                  {conversation.conversationType === 'DIRECT_MESSAGE' ? <FaPaperPlane /> : <FaTicketAlt />}
                </span>
                <span className="messages-conv-main">
                  <span className="messages-conv-title">{conversation.subject || conversation.title}</span>
                  <span className="messages-conv-last">{conversation.lastMessagePreview || statusLabel(conversation.status)}</span>
                </span>
                <span className="messages-conv-meta">
                  <span>{formatRelative(conversation.lastMessageAt || conversation.updatedAt || conversation.createdAt)}</span>
                  {!!conversation.unreadCount && <strong>{conversation.unreadCount}</strong>}
                </span>
              </button>
            ))
          )}
          {hasMore && (
            <button className="messages-load-more" onClick={loadMore} disabled={loadingList}>
              Load more
            </button>
          )}
        </div>
      </aside>

      <main className="messages-chat">
        {!selectedConversation ? (
          <div className="messages-empty-thread">
            <div className="messages-empty-art" aria-hidden="true"><FaEnvelopeOpen/></div><h2>{loadingThread ? 'Loading conversation…' : 'Your research conversations'}</h2><p>Choose a chat to continue a conversation with your colleagues or the support team.</p>
          </div>
        ) : (
          <>
            <header className="messages-chat-header"><button className="messages-back" aria-label="Back to conversations" onClick={()=>setSelectedConversationId(null)}>←</button>
              <div>
                <small>REA-SUP-{String(selectedConversation.id).padStart(4,'0')}</small><h2>{selectedConversation.subject || selectedConversation.title}</h2>
                <div className="messages-thread-meta">
                  <span className={`messages-status ${selectedConversation.status.toLowerCase()}`}>
                    {statusLabel(selectedConversation.status)}
                  </span>
                  {selectedConversation.researchApplicationId && (
                    <span>Application #{selectedConversation.researchApplicationId}</span>
                  )}
                  {selectedConversation.assignedSupportUserName && (
                    <span>Assigned to {selectedConversation.assignedSupportUserName}</span>
                  )}
                </div>
              </div>
              <div className="messages-header-actions">
                <button className="messages-icon-btn" onClick={() => fetchConversation(selectedConversation.id)} title="Refresh">
                  <FaRedo />
                </button>
                <button className="messages-icon-btn" onClick={() => fetch(`/v1/api/support/conversations/${selectedConversation.id}/read?userId=${currentUserId}`, { method: 'PUT' }).then(() => fetchConversation(selectedConversation.id, true))} title="Mark read">
                  <FaCheck />
                </button>
                {canManageSelected && selectedConversation.status !== 'CLOSED' && (
                  <button className="messages-action-btn" onClick={() => handleStatusAction('close')}>Close</button>
                )}
                {canManageSelected && ['CLOSED','RESOLVED'].includes(selectedConversation.status) && (
                  <button className="messages-action-btn" onClick={() => handleStatusAction('reopen')}>Reopen</button>
                )}
              </div>
            </header>

            {(isSupportUser||selectedConversation.assignedSupportUserId===currentUserId) && selectedConversation.conversationType!=='DIRECT_MESSAGE' && (
              <div className="messages-assignment-row">
                {!selectedConversation.assignedSupportUserId&&<button className="ui-button ui-button--primary" onClick={()=>handleAssign(String(currentUserId))}>Assign to me</button>}
                {selectedConversation.assignedSupportUserId===currentUserId&&<><button className="ui-button ui-button--primary" onClick={()=>handleStatusAction('resolve')}>Resolve</button><button className="ui-button" onClick={()=>handleStatusAction('release')}>Release assignment</button></>}
                <FaUserCheck />
                <select
                  value={selectedConversation.assignedSupportUserId || ''}
                  onChange={(event) => handleAssign(event.target.value)}
                >
                  <option value="">Assign support agent</option>
                  {supportUsers.map((user) => (
                    <option key={user.id} value={user.id}>{displayUser(user)}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="messages-chat-body">
              {selectedConversation.hasMoreMessages&&<button className="ui-button messages-load-earlier" onClick={()=>fetchConversation(selectedConversation.id,false,messagePage+1)}>Load earlier messages</button>}
              {loadingThread ? (
                <div className="messages-state">Loading messages...</div>
              ) : selectedMessages.length === 0 ? (
                <div className="messages-empty-thread">No messages yet.</div>
              ) : (
                selectedMessages.map((message) => {
                  const mine = message.senderUserId === currentUserId;
                  return (
                    <div className={`messages-msg-row${mine ? ' me' : ''}`} key={message.id}>
                      <UserAvatar userId={message.senderUserId} name={message.senderUserName}/><div className="messages-msg-bubble">
                        <div className="messages-msg-author">{mine ? 'You' : message.senderUserName}</div><small>{roleLabel(message.senderRole||'')}</small>
                        <div className="messages-msg-text">{message.body}</div>
                        {message.attachmentOriginalFileName && message.attachmentDownloadUrl && (
                          <a
                            onClick={async e=>{e.preventDefault();const response=await fetch(`${message.attachmentDownloadUrl}?userId=${currentUserId}`);if(!response.ok){addToast("Unable to download attachment.");return;}const url=URL.createObjectURL(await response.blob());const anchor=document.createElement("a");anchor.href=url;anchor.download=message.attachmentOriginalFileName||"attachment";anchor.click();setTimeout(()=>URL.revokeObjectURL(url),30000);}}
                            className="messages-attachment-link"
                            href={`${message.attachmentDownloadUrl}?userId=${currentUserId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FaPaperclip />
                            {message.attachmentOriginalFileName}
                            {message.attachmentFileSize ? ` (${formatBytes(message.attachmentFileSize)})` : ''}
                          </a>
                        )}
                        <span className="messages-msg-time">{formatDateTime(message.sentAt)}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="messages-composer">
              {!canReply ? (
                <div className="messages-closed-note">{selectedIsClosed?"This conversation is closed or resolved.":"Assign this support case to yourself before replying."}</div>
              ) : (
                <>
                  <textarea aria-label="Type your message"
                    placeholder="Type your message"
                    value={composerBody}
                    maxLength={MAX_BODY_LENGTH}
                    onChange={(event) => setComposerBody(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <div className="messages-composer-actions">
                    <label className="messages-file-btn">
                      <FaPaperclip />
                      <span>{composerAttachment ? composerAttachment.name : 'Attach'}</span>
                      <input type="file" onChange={(event) => handleAttachmentChange(event.target.files?.[0])} />
                    </label>
                    <span className="messages-char-count">{composerBody.length}/{MAX_BODY_LENGTH}</span>
                    <button className="messages-send-btn" onClick={handleSend} disabled={sending || !composerBody.trim()}>
                      <FaPaperPlane />
                      <span>{sending ? 'Sending' : 'Send'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </main>

      {showNewModal && (
        <ModalLayer className="messages-modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="messages-modal" onClick={(event) => event.stopPropagation()}>
            <button className="messages-modal-close" onClick={() => setShowNewModal(false)} aria-label="Close">
              <FaTimes />
            </button>
            <h2>New Conversation</h2>
            <label>
              Subject
              <input value={newSubject} onChange={(event) => setNewSubject(event.target.value)} />
            </label>
            <label>
              Type
              <select value={newType} onChange={(event) => setNewType(event.target.value as ConversationType)}>
                <option value={currentUserType === 'APPLICANT' ? 'APPLICANT_SUPPORT' : 'USER_SUPPORT'}>Support conversation</option>
                <option value="APPLICATION_SUPPORT">Application support</option>
                {canDirect && <option value="DIRECT_MESSAGE">Direct user message</option>}
              </select>
            </label>
            {(newType === 'APPLICATION_SUPPORT') && (
              <label>
                Application ID
                <input
                  inputMode="numeric"
                  value={newApplicationId}
                  onChange={(event) => setNewApplicationId(event.target.value.replace(/\D/g, ''))}
                />
              </label>
            )}
            {newType === 'DIRECT_MESSAGE' && canDirect && (
              <label>
                Recipient
                <select value={directRecipientId} onChange={(event) => setDirectRecipientId(event.target.value)}>
                  <option value="">Select recipient</option>
                  {directMessageUsers.map((user) => (
                    <option key={user.id} value={user.id}>{displayUser(user)}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Message
              <textarea rows={5} value={newBody} maxLength={MAX_BODY_LENGTH} onChange={(event) => setNewBody(event.target.value)} />
            </label>
            <div className="messages-ticket-modal-btns">
              <button className="messages-cancel-btn" onClick={() => setShowNewModal(false)}>Cancel</button>
              <button
                className="messages-submit-btn"
                onClick={handleCreateConversation}
                disabled={!newSubject.trim()
                  || !newBody.trim()
                  || (newType === 'DIRECT_MESSAGE' && !directRecipientId)
                  || (newType === 'APPLICATION_SUPPORT' && !newApplicationId)}
              >
                Create
              </button>
            </div>
          </div>
        </ModalLayer>
      )}
    </div>
  );
};

function displayUser(user: UserOption): string {
  const name = `${user.name || ''} ${user.surname || ''}`.trim() || `User #${user.id}`;
  return user.userType ? `${name} (${user.userType.replaceAll('_', ' ')})` : name;
}

function statusLabel(status?: string): string {
  if (!status) return 'Open';
  if (status === 'PENDING') return 'Pending';
  if (status === 'CLOSED') return 'Closed';
  return status.replace(/_/g,' ').toLowerCase().replace(/\b\w/g,c=>c.toUpperCase());
}

function formatDateTime(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function formatRelative(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'Now';
  if (diffMinutes < 60) return `${diffMinutes}m`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default MessagesPage;
